import { invariant } from '@wyz/utils';
import { SUPPLIERS_STATUSES } from '~/connect/schemas';
import {
  ICriteriaList,
  IRedistributionSupplierTemplate,
  IRedistributionSupplierTemplateForm,
  IRedistributionSupplierTemplatePayload,
} from '~/connect/types';

export const tryReorderSuppliers = (
  suppliers: IRedistributionSupplierTemplateForm['suppliers'],
  from: number,
  to: number,
): IRedistributionSupplierTemplateForm['suppliers'] | null => {
  const item = suppliers[from];
  if (!item || !item._internalStock) return null;

  const newSuppliers = [...suppliers];
  newSuppliers.splice(from, 1);
  newSuppliers.splice(to, 0, item);

  const firstTemplateIndex = newSuppliers.findIndex((s) => !s._internalStock);
  if (firstTemplateIndex === -1) return newSuppliers;

  const lastTemplateIndex =
    newSuppliers.length -
    1 -
    [...newSuppliers].reverse().findIndex((s) => !s._internalStock);

  const hasInternalStockBetweenTemplates = newSuppliers
    .slice(firstTemplateIndex, lastTemplateIndex + 1)
    .some((s) => s._internalStock);

  if (hasInternalStockBetweenTemplates) return null;

  return newSuppliers;
};

const getCriterionValuesByIds = (
  criterionId: string,
  criterionValueIds: Array<string>,
  allCriteria: ICriteriaList['data'],
): IRedistributionSupplierTemplatePayload['criteria'][number]['values'] => {
  const criterion = allCriteria.find((item) => item.id === criterionId);
  invariant(criterion);
  return criterion.values.filter((criterionValue) =>
    criterionValueIds.includes(criterionValue.id),
  );
};

export const rawToForm = (
  document: IRedistributionSupplierTemplate,
): IRedistributionSupplierTemplateForm => {
  return {
    ...document,
    customerScope: document.customerScope?.at(0)?.id || '',
    criteria: document.criteria.map((associatedCriterion) => ({
      ...associatedCriterion,
      values: associatedCriterion.values.map(
        (associatedCriterionValue) => associatedCriterionValue.id,
      ),
    })),
    suppliers: document.suppliers.map((supplier) => ({
      code: supplier.code ?? null,
      wyzApiId: supplier.wyzApiId,
      name: supplier.name ?? '',
      type: supplier.type,
      orderable: supplier.orderable,
      overridePriorityIfShorterDelivery:
        supplier.overridePriorityIfShorterDelivery ?? null,
      customName: supplier.customName,
      status: supplier.status,
      enabled: supplier.status === SUPPLIERS_STATUSES.ENABLED,
      rankingModelId: supplier.rankingModelId ?? null,
      childCustomerDelivery: supplier.childCustomerDelivery ?? false,
      autoValidation: supplier.autoValidation ?? false,
      bestPriceDelay: supplier.bestPriceDelay ?? null,
      extendedManufConfiguration: supplier.extendedManufConfiguration ?? null,
      customerId: supplier.customerId ?? null,
      _internalStock: supplier._internalStock ?? false,
    })),
  };
};
export const formToPayload = (
  formData: IRedistributionSupplierTemplateForm,
  allCriteria: ICriteriaList['data'],
): IRedistributionSupplierTemplatePayload => {
  return {
    name: formData.name,
    customerScope: formData.customerScope
      ? [{ id: formData.customerScope }]
      : [],
    criteria: formData.criteria.map((associatedCriterion) => ({
      ...associatedCriterion,
      values: getCriterionValuesByIds(
        associatedCriterion.criterion.id,
        associatedCriterion.values,
        allCriteria,
      ),
    })),
    suppliers: (formData.suppliers ?? []).map((supplier) => ({
      wyzApiId: supplier.wyzApiId,
      orderable: supplier.orderable ?? true,
      customName: supplier.customName,
      type: supplier.type,
      status:
        supplier.enabled === true
          ? SUPPLIERS_STATUSES.ENABLED
          : SUPPLIERS_STATUSES.DISABLED,
      customerId: supplier.customerId ?? null,
      overridePriorityIfShorterDelivery:
        supplier.overridePriorityIfShorterDelivery ?? true,
      rankingModelId: supplier.rankingModelId ?? null,
      childCustomerDelivery: supplier.childCustomerDelivery ?? false,
      autoValidation: supplier.autoValidation ?? false,
      extendedManufConfiguration: supplier.extendedManufConfiguration
        ? {
            enabled: supplier.extendedManufConfiguration.enabled ?? false,
            warehouseId: supplier.extendedManufConfiguration.warehouseId ?? '',
            displayName: supplier.extendedManufConfiguration.displayName ?? '',
          }
        : null,
    })),
  };
};
